import { OrderResponseRecord, PapelDeVenta, PaymentSplit, PosTicketRequest } from '../../models/api.models';
import { traducirMedioPago } from '../../models/order-status';
import type { ComprobanteDeSede, ResponsabilidadFiscal } from '../../services/table.service';
import { TarifaDelCatalogo, cobradoPorLinea, desglosePorTarifa } from './impuestos-del-papel';

/**
 * La responsabilidad fiscal en palabras (Producto 81517d3, ⚖ hasta el visto del Contador); sin ficha (null) no sale.
 */
export function textoDeResponsabilidad(r: ResponsabilidadFiscal | null | undefined): string | null {
    if (!r) return null;
    const partes = [
        r.responsableIva === true ? 'Responsable de IVA' : r.responsableIva === false ? 'No responsable de IVA' : null,
        r.responsableInc === true ? 'Responsable de INC' : null,
        r.regimen === 'SIMPLE' ? 'Régimen simple de tributación' : null,
    ].filter((x): x is string => !!x);
    return partes.join(' · ') || null;
}

/** El papel sin nada de la plantilla (un -mt anterior a A3), para colgarle solo la factura. */
export const papelVacio = (): PapelDeVenta => ({
    encabezado: null, razonSocial: null, responsabilidad: null, correo: null, sede: null, vendedor: null, mesa: null,
    descuento: null, articulos: null, impuestos: [], pagos: [], codigos: [], efectivoYCambio: true,
});

/** Lo que la plantilla necesita de fuera de la orden. */
export interface ContextoDelPapel {
    comprobante: ComprobanteDeSede | null | undefined;
    catalogo: TarifaDelCatalogo[];
    /** El producto de una línea (sin red, de ahí salen su tarifa y su código). */
    producto: (id: string) => { impuestoCodigo?: string | null; codigos?: { codigo: string }[] } | undefined;
    conMeseros: boolean;
    /** El reparto del mixto, si se conoce (al cobrar); al reimprimir desde el historial no viaja. */
    pagos?: PaymentSplit[] | null;
}

/**
 * M34 A7 · aplica la plantilla de A3 (`comprobante` de `/account/sites/mode`) a la tirilla de la vía del navegador.
 * Sin plantilla (un -mt anterior a A3) el papel sale como hoy, con la fecha real si la hay. Oculta lo que el comercio
 * desmarcó y añade en `papel` lo nuevo; lo que no tiene dato no sale.
 */
export function aplicarComprobante(t: PosTicketRequest, orden: OrderResponseRecord, ctx: ContextoDelPapel): PosTicketRequest {
    if (orden.ocurridoEn) t.dateTime = orden.ocurridoEn;
    const c = ctx.comprobante;
    const m = c?.mostrar;
    if (!c || !m) return t;

    const n = c.negocio ?? {};
    if (n.nombreComercial) t.businessName = n.nombreComercial;
    const nit = n.nit ?? t.nit;
    t.nit = m.nit && nit ? (n.dv ? `${nit}-${n.dv}` : nit) : '';
    t.address = m.direccion ? (c.sede?.direccion ?? t.address) : '';
    t.phone = m.telefono ? (n.telefono ?? t.phone) : '';
    if (!m.cajero) t.cashierName = '';
    if (!m.cliente) { t.customerName = undefined; t.customerId = undefined; }

    const items = orden.items ?? [];
    const cobrado = cobradoPorLinea(items.map((i) => Number(i.totalPrice) || 0), orden.discountAmount);
    const lineas = items.map((i, k) => ({
        impuestoCodigo: i.impuestoCodigo !== undefined && i.impuestoCodigo !== null ? i.impuestoCodigo : ctx.producto(i.productId)?.impuestoCodigo,
        impuestoBase: i.impuestoBase, impuestoValor: i.impuestoValor,
        cobrado: cobrado[k], cantidad: i.precioPorKilo != null ? 1 : i.quantity,
    }));
    const pagos = orden.paymentMethod === 'MIXED' ? (ctx.pagos ?? []).filter((p) => p.amount > 0) : [];
    const papel: PapelDeVenta = {
        encabezado: c.encabezado?.trim() || null,
        razonSocial: m.razonSocial ? n.razonSocial || null : null,
        responsabilidad: m.responsabilidad ? textoDeResponsabilidad(n.responsabilidad) : null,
        correo: m.correo ? n.correo || null : null,
        sede: m.sede ? c.sede?.nombre || null : null,
        vendedor: m.vendedor && orden.waiterName ? { etiqueta: ctx.conMeseros ? 'Mesero' : 'Vendedor', nombre: orden.waiterName } : null,
        mesa: m.mesa ? orden.tableLabel || (orden.tableNumber != null ? String(orden.tableNumber) : null) : null,
        descuento: m.descuentos && (orden.discountAmount ?? 0) > 0 ? orden.discountAmount! : null,
        articulos: m.cantidadDeArticulos ? items.reduce((a, i) => a + (i.precioPorKilo != null ? 1 : i.quantity), 0) : null,
        impuestos: m.desglosePorTarifa ? desglosePorTarifa(lineas, ctx.catalogo) : [],
        pagos: pagos.map((p) => ({ medio: traducirMedioPago(p.method), valor: p.amount })),
        codigos: items.map((i) => (m.codigoProducto ? ctx.producto(i.productId)?.codigos?.[0]?.codigo ?? null : null)),
        efectivoYCambio: m.efectivoYCambio,
    };
    t.papel = papel;
    return t;
}
